import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, LayoutGrid, Heart, Sparkles, User, Package, Share, Plus, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api";

export function applyStickerBg(value) {
  if (value) document.documentElement.style.setProperty("--sticker-bg", value);
}

const navItems = [
  { to: "/", label: "Início", icon: Home, testid: "nav-home" },
  { to: "/categorias", label: "Categorias", icon: LayoutGrid, testid: "nav-categories" },
  { to: "/packs", label: "Packs", icon: Package, testid: "nav-packs" },
  { to: "/favoritos", label: "Favoritos", icon: Heart, testid: "nav-favorites" },
  { to: "/conta", label: "Conta", icon: User, testid: "nav-account" },
];

function InstallBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const dismissed = localStorage.getItem("bc_install_dismissed");
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (!dismissed && !standalone) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }
  }, []);
  useEffect(() => {
    document.body.classList.toggle("has-install-banner", visible);
    return () => document.body.classList.remove("has-install-banner");
  }, [visible]);
  if (!visible) return null;
  return (
    <div
      data-testid="install-banner"
      className="lg:hidden fixed bottom-20 left-3 right-3 z-40 mx-auto max-w-md bg-white rounded-3xl p-4 border border-[#F2E6E7] bc-shadow-hover animate-float-up"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-[#F2E6E7] flex items-center justify-center shrink-0">
          <Share size={18} className="text-[#C58B90]" />
        </div>
        <div className="flex-1">
          <p className="font-serif text-base text-[#2C2A29]">Adicionar à Tela de Início</p>
          <p className="text-xs text-[#7A7571] mt-0.5 leading-relaxed">
            Use o Brenda Club como app! Toque em <span className="font-semibold">Compartilhar</span> e depois em{" "}
            <span className="font-semibold">"Adicionar à Tela de Início"</span> <Plus size={11} className="inline" />
          </p>
        </div>
        <button
          data-testid="install-banner-close"
          onClick={() => {
            localStorage.setItem("bc_install_dismissed", "1");
            setVisible(false);
          }}
          className="text-[#7A7571] p-1"
          aria-label="Fechar"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => applyStickerBg(r.data.sticker_bg)).catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen pb-24 lg:pb-8">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#FCFBF9]/85 border-b border-[#F2E6E7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/")} data-testid="header-logo-btn">
            <Logo size={40} showText />
          </button>

          {/* Desktop navigation — site look on larger screens */}
          <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`desktop-${item.testid}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-[#F2E6E7] text-[#C58B90]" : "text-[#2C2A29] hover:bg-[#F7EBEC]"
                  }`
                }
              >
                <item.icon size={17} /> {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/novidades"
              data-testid="desktop-nav-novidades"
              className={({ isActive }) =>
                `inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "bg-[#F2E6E7] text-[#C58B90]" : "text-[#2C2A29] hover:bg-[#F7EBEC]"
                }`
              }
            >
              <Sparkles size={17} /> Novidades
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/novidades")}
              data-testid="header-novidades-btn"
              className="lg:hidden h-10 w-10 rounded-full bg-[#F2E6E7] flex items-center justify-center text-[#C58B90] transition-colors hover:bg-[#E8CFD1]"
              aria-label="Novidades"
            >
              <Sparkles size={18} />
            </button>
            {user?.role === "admin" && (
              <button
                onClick={() => navigate("/admin")}
                data-testid="header-admin-btn"
                className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#C58B90] text-white text-sm font-medium transition-colors hover:bg-[#A87277]"
              >
                Painel
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">{children}</main>

      <InstallBanner />

      <nav
        data-testid="bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 backdrop-blur-xl bg-white/90 border-t border-[#F2E6E7]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-6xl mx-auto px-2 h-16 flex items-center justify-around">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-2xl transition-colors ${
                  isActive ? "text-[#C58B90]" : "text-[#7A7571]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={21} className={isActive ? "fill-[#F2E6E7]" : ""} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
